import { useContext, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Context } from "../context";
import { Button, Pagination, Modal, Segmented, Spin } from 'antd'
import React, { useState } from 'react';
import { TableProject } from './TableProject';
import { TableSettingsLangs } from './TableSettingsLangs';
import { NewProjectDialog } from "./NewProjectDialog";
import { Head } from "./Head";
import { ProjectsSearchSort } from "./ProjectsSearchSort";
import { ClipsDrawer } from "./ClipsDrawer";
import { BulkProcessBar } from "./BulkProcessBar";

export function Table({ onLogout, ...props }) {

  const navigate = useNavigate();

  const context = useContext(Context);
  const { langsShown, setLangsShown } = context.state;

  // Use context pagination instead of local state
  const { currentPage, totalPages, pageSize, isLoading, totalItems } = context.getPaginationInfo();

  // Check if data is still loading (initial fetch not done yet)
  const isDataLoading = !context.state.ready;

  const onPageChange = (page) => {
    context.goToPage(page);
  };

  const onShowSizeChange = (current, size) => {
    context.changePageSize(size);
  };

  const handleChange = (value) => {
    // Top language chips now FILTER the list (lectures that have the language) + set the column view.
    // handleLangFilter also calls setLangsShown; fall back to it if the handler isn't present.
    if (context.handleLangFilter) {
      context.handleLangFilter(value);
    } else {
      setLangsShown(value);
    }
  };

  const [isModalOpen, setIsModalOpen] = useState(false);

  const showModal = () => {
    setIsModalOpen(true);
  };

  const handleOk = () => {
    setIsModalOpen(false);
  };

  const handleCancel = () => {
    setIsModalOpen(false);
  };

  const [projectStatus, setProjectStatus] = useState('all');

  const [projectView, setProjectView] = useState('all');

  // fid whose clips drawer is open (opened from a row's clip-count badge)
  const [clipsFid, setClipsFid] = useState(null);

  // Memoize filtered projects to avoid re-filtering on every render
  const filteredProjects = useMemo(() => {
    if (isDataLoading || isLoading) return [];

    return context.data
      .filter(oneProject => {
        if (projectStatus === 'all') return true;
        if (projectStatus === 'notArchived') return !oneProject.project_archived;
        if (projectStatus === 'archived') return oneProject.project_archived;
        return false;
      })
      .filter(oneProject => {
        // NB: in the list (lightweight) response `parts` is empty; parts_count is the
        // real signal. "Есть части"/"Пустые" are current-page-only display filters.
        const partsCount = oneProject.parts_count ?? (oneProject.parts ? oneProject.parts.length : 0);
        if (projectView === 'all') return true;
        if (projectView === 'withParts') return partsCount > 0;
        if (projectView === 'empty') return partsCount === 0;
        return false;
      });
  }, [context.data, projectStatus, projectView, isDataLoading, isLoading]);

  // "Только переведённые" is a SERVER-side filter (context) — orthogonal to the
  // client-side all/withParts/empty view toggle. Selecting it clears the local view.
  const segmentedValue = context.state.translatedFilter ? 'translated' : projectView;
  const handleViewChange = (val) => {
    if (val === 'translated') {
      setProjectView('all');
      context.handleTranslatedFilter(true);
    } else {
      if (context.state.translatedFilter) {
        context.handleTranslatedFilter(false);
      }
      setProjectView(val);
    }
  };

  return (
    <>

      {/* Временно скрыто - кнопка создания проекта
      <Modal
        title="Создать проект"
        open={isModalOpen}
        onOk={handleOk}
        onCancel={handleCancel}
      >
        <NewProjectDialog />
      </Modal>
      */}


      <Head activeTab="tasks" onLogout={onLogout}>

        {/* 

          <Segmented
            value={projectStatus}
            onChange={setProjectStatus}
            options={[
              {
                label: <div className="_f">
                  Все проекты
                </div>,
                value: 'all',
              },
              {
                label:
                  <div>
                    Актуальные
                  </div>,
                value: 'notArchived',
              },
              {
                label:
                  <div>
                    Архив
                  </div>,
                value: 'archived',
              },
            ]}
          /> */}


        <Segmented
          value={segmentedValue}
          onChange={handleViewChange}
          options={[
            {
              label: <div className="_f">
                Все проекты
              </div>,
              value: 'all',
            },
            {
              label:
                <div>
                  Только переведённые
                </div>,
              value: 'translated',
            },
            {
              label:
                <div>
                  Есть части
                </div>,
              value: 'withParts',
            },
            {
              label:
                <div>
                  Пустые
                </div>,
              value: 'empty',
            },
          ]}
        />

        <TableSettingsLangs value={langsShown} handleChange={handleChange} />

        {/* Временно скрыто - кнопка создания проекта
        <Button type="primary" size="middle" onClick={showModal}>
          <div>Создать проект</div>
        </Button>
        */}


      </Head>

      <div style={{ padding: '16px 16px 0 16px' }}>
        <ProjectsSearchSort />
      </div>

      <BulkProcessBar />

      <div className="t00" style={{ width: '100%', background: '#e9e9e9', flexGrow: 1, }}>

        <Spin
          spinning={isDataLoading || isLoading}
          tip="Загрузка..."
          size="large"
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '200px',
            rowGap: '1em',
            padding: '0',
          }}
        >

          <div className="_fv _fgap1" style={{ width: '100%' }}>

            {(isDataLoading || isLoading) && <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: '72vh',
                flexGrow: 1,
              }}
            />}

            {!isLoading && filteredProjects.map((oneProject) => (
              <TableProject
                langsShown={langsShown}
                key={`project_${oneProject._id}`}
                onOpenClips={setClipsFid}
                {...oneProject}
              />
            ))}

          </div>

        </Spin>

        <div
          style={{
            padding: '1em',
            position: 'sticky',
            bottom: '0',
            marginBottom: '-1em',
            marginLeft: '-1em',
            marginRight: '-1em',
            zIndex: 100,
            background: '#cecece',
            // borderRadius: '0.6em',
          }}
        >
          <Pagination
            current={currentPage}
            pageSize={pageSize}
            pageSizeOptions={[]}
            showSizeChanger={false}
            total={totalItems}
            onChange={onPageChange}
            showQuickJumper
            showTotal={(total, range) => `${range[0]}-${range[1]} из ${total} проектов`}
          />
        </div>

      </div>

      <ClipsDrawer
        projectId={clipsFid}
        open={!!clipsFid}
        onClose={() => setClipsFid(null)}
      />

    </>
  )
}
